import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OnboardingFacade } from '../../../../../Facades/onboarding.facade';
import { Plan, PlanEntitlement, PlanPrice } from '../../../../../Interfaces/plan.interface';

@Component({
  selector: 'app-plan-selection',
  standalone: true,
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslateModule,
  ],
  templateUrl: './plan-selection.component.html',
  styleUrl: './plan-selection.component.scss',
})
export class PlanSelectionComponent implements OnInit {
  private planService = inject(OnboardingFacade);
  private translate = inject(TranslateService);
  private router = inject(Router);

  plans = signal<Plan[]>([]);
  selectedPlanId = signal<string | null>(null);
  offerType = signal<'monthly' | 'commit12'>('monthly');
  isLoading = signal(true);
  isSubmitting = signal(false);
  errorMessage = signal<string | null>(null);

  readonly sortedPlans = computed(() =>
    [...this.plans()].sort((a, b) => this.seatAmount(a) - this.seatAmount(b))
  );

  readonly maxDiscountPercent = computed(() => {
    const discounts = this.plans()
      .map((plan) => this.getDiscountPercent(plan))
      .filter((percent): percent is number => percent !== null && percent > 0);

    return discounts.length > 0 ? Math.max(...discounts) : null;
  });

  ngOnInit(): void {
    this.loadPlans();
  }

  private seatAmount(plan: Plan): number {
    return this.getSeatPrice(plan)?.unit_amount ?? Number.MAX_SAFE_INTEGER;
  }

  private loadPlans(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.planService.getPlans({ skipLoading: true }).subscribe({
      next: (plans) => {
        this.plans.set(plans);
        this.isLoading.set(false);
      },
      error: (err: Error) => {
        this.errorMessage.set(
          err.message || this.translate.instant('onboarding.setup.planSelection.errors.loadFailed')
        );
        this.isLoading.set(false);
      },
    });
  }

  retry(): void {
    this.loadPlans();
  }

  setOfferType(type: 'monthly' | 'commit12'): void {
    this.offerType.set(type);
  }

  getSeatPrice(plan: Plan): PlanPrice | undefined {
    return plan.prices.find(
      (p) => p.component_type === 'seat' && p.offer_type === this.offerType()
    );
  }

  getAgentPrice(plan: Plan): PlanPrice | undefined {
    return plan.prices.find(
      (p) => p.component_type === 'agent' && p.offer_type === this.offerType()
    );
  }

  getMonthlySeatPrice(plan: Plan): PlanPrice | undefined {
    return plan.prices.find(
      (p) => p.component_type === 'seat' && p.offer_type === 'monthly'
    );
  }

  getDiscountPercent(plan: Plan): number | null {
    const monthlySeat = this.getMonthlySeatPrice(plan);
    const commit12Seat = plan.prices.find(
      (p) => p.component_type === 'seat' && p.offer_type === 'commit12'
    );

    if (!monthlySeat || !commit12Seat) return null;

    const percent = Math.round((1 - commit12Seat.unit_amount / monthlySeat.unit_amount) * 100);
    return percent > 0 ? percent : null;
  }

  getEntitlements(plan: Plan): PlanEntitlement[] {
    return [...(plan.entitlements ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  }

  getFeatureLabelKey(entitlement: PlanEntitlement): string {
    return `onboarding.features.${entitlement.key}`;
  }

  isCatalogShowcase(entitlement: PlanEntitlement): boolean {
    return entitlement.key === 'catalog';
  }

  getDescriptionKey(plan: Plan): string {
    const key = `onboarding.setup.planSelection.descriptions.${plan.code.toLowerCase()}`;

    return this.translate.instant(key) === key
      ? 'onboarding.setup.planSelection.descriptions.fallback'
      : key;
  }

  formatAmount(unitAmount: number): string {
    const n = Number(unitAmount);
    return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2);
  }

  getCurrencySymbol(currency: string): string {
    const symbols: Record<string, string> = { eur: '€', usd: '$', gbp: '£' };
    return symbols[currency.toLowerCase()] ?? currency.toUpperCase();
  }

  selectPlan(plan: Plan): void {
    if (this.isSubmitting()) return;

    this.selectedPlanId.set(plan.id);
    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    this.planService.savePlan(plan.id, this.offerType()).subscribe({
      next: () => {
        this.router.navigate(['/setup/licences']);
      },
      error: (err: Error) => {
        this.isSubmitting.set(false);
        this.errorMessage.set(
          err.message || this.translate.instant('onboarding.setup.planSelection.errors.saveFailed')
        );
      },
    });
  }
}
